import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  DescriptionList,
  ErrorState,
  InlineLoading,
  LoadingState,
  Page,
  ResponsiveGrid,
  Text,
} from "@marekl/adminer/components";
import { formatDateTime, pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { competitionCrumbs, useCompetitionReadOnly } from "../../../shell/CompetitionLayout";
import { useSeasons } from "../../../lib/queries/memberships";
import { useCompetition } from "../../../lib/queries/competitions";
import { useCompetitionEntries } from "../../../lib/queries/entries";
import {
  COMPETITION_TYPE_LABELS,
  daysLeftLabel,
  ENTRY_PHASE_LABELS,
  ENTRY_PHASE_VARIANT,
  formatCompetitionDates,
} from "../../../lib/competitionState";
import { CompetitionLifecycle } from "../../../components/CompetitionLifecycle";

export function CompetitionOverviewPage() {
  const { id } = useParams();
  // A competitions_viewer reads this page; nothing on it is offered.
  const readOnly = useCompetitionReadOnly();
  const competitionId = id ?? "";
  const { data: competition, isLoading, error, refetch } = useCompetition(competitionId);
  const { data: seasons = [] } = useSeasons();
  // The page's own gate covers the competition, not this: without the flag
  // „Podáno" reads 0 for a competition with a full field.
  const { data: entries = [], isLoading: entriesLoading } =
    useCompetitionEntries(competitionId);
  const withdrawnCount = entries.filter((e) => e.status === "withdrawn").length;

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={competitionCrumbs(competitionId, undefined)} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={competitionCrumbs(competitionId, undefined)} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const seasonCode = seasons.find((s) => s.id === competition.season_id)?.code;
  const deadline = competition.entry_deadline;
  const daysLeft = deadline ? daysLeftLabel(deadline) : null;
  const contact = [
    competition.contact_name,
    competition.contact_phone,
    competition.contact_email,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <AppTopbar crumbs={competitionCrumbs(competitionId, competition)} />
      <Page>
        <Page.Header
          title={competition.name}
          subtitle={[
            COMPETITION_TYPE_LABELS[competition.type],
            competition.series_name,
            seasonCode,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <Link to={`/soutez/${competition.id}/nastaveni`}>
              <Button>Nastavení</Button>
            </Link>
          }
        />
        <Page.Content stack gap="lg">
          <CompetitionLifecycle competition={competition} readOnly={readOnly} />

          <ResponsiveGrid columns={2}>
            <Card>
              <Card.Header title="Základní údaje" />
              <Card.Content>
                <DescriptionList>
                  <DescriptionList.Item label="Datum">{formatCompetitionDates(competition)}</DescriptionList.Item>
                  <DescriptionList.Item label="Místo">
                    {competition.venue || <Text variant="muted">—</Text>}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Pořadatel">
                    {competition.organizer_name || <Text variant="muted">—</Text>}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Kontakt">
                    {contact || <Text variant="muted">—</Text>}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Sada pravidel">
                    {competition.rule_set_name}
                  </DescriptionList.Item>
                </DescriptionList>
              </Card.Content>
            </Card>
            <Card>
              <Card.Header title="Přihlášky" />
              <Card.Content>
                <DescriptionList>
                  <DescriptionList.Item label="Podáno">
                    {entriesLoading ? (
                      <InlineLoading>Načítám…</InlineLoading>
                    ) : (
                      <>
                        {entries.filter((e) => e.status === "submitted").length}
                        {entries.some((e) => e.status === "withdrawn")
                          ? ` (+ ${withdrawnCount} ${pluralizeCzech(withdrawnCount, [
                              "odhláška",
                              "odhlášky",
                              "odhlášek",
                            ])})`
                          : ""}
                      </>
                    )}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Uzávěrka přihlášek">
                    {deadline ? formatDateTime(deadline) : <Text variant="muted">nenastavena</Text>}
                  </DescriptionList.Item>
                  {daysLeft !== null && competition.entry_phase === "open" && (
                    <DescriptionList.Item label="Zbývá">{daysLeft}</DescriptionList.Item>
                  )}
                  {/* Shown only when the organizer set them: three
                      identical dates would say the taper exists here when
                      it does not. The effective values drive everything,
                      but what is worth reading back is the choice. */}
                  {competition.entry_category_deadline && (
                    <DescriptionList.Item label="Uzávěrka změn kategorie">
                      {formatDateTime(competition.entry_category_deadline)}
                    </DescriptionList.Item>
                  )}
                  {competition.entry_roster_deadline && (
                    <DescriptionList.Item label="Uzávěrka změn soutěžících">
                      {formatDateTime(competition.entry_roster_deadline)}
                    </DescriptionList.Item>
                  )}
                  <DescriptionList.Item label="Max. kapacita">
                    {competition.max_capacity ?? "bez omezení"}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Stav přihlašování">
                    <Badge variant={ENTRY_PHASE_VARIANT[competition.entry_phase]}>
                      {ENTRY_PHASE_LABELS[competition.entry_phase]}
                    </Badge>
                  </DescriptionList.Item>
                </DescriptionList>
              </Card.Content>
            </Card>
          </ResponsiveGrid>
        </Page.Content>
      </Page>
    </>
  );
}
